import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const GamificationTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [gamificationData, setGamificationData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [userEmailMap, setUserEmailMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch gamification data and users in parallel
        const [data, users] = await Promise.all([
          getCollection('user_gamification', undefined, undefined, undefined, 1000),
          getCollection('users', undefined, undefined, undefined, 10000).catch(() => []),
        ]);

        // Create a map of userId -> email
        const emailMap = new Map<string, string>();
        users.forEach((user: any) => {
          if (user.id && user.email) {
            emailMap.set(user.id, user.email);
          }
        });
        setUserEmailMap(emailMap);

        setGamificationData(data);

        // Calculate statistics
        if (data.length > 0) {
          const totalXP = data.reduce((sum: number, user: any) => sum + (user.totalXP || 0), 0);
          const avgXP = totalXP / data.length;
          const maxXP = Math.max(...data.map((user: any) => user.totalXP || 0));

          // Level distribution
          const levelDistribution: Map<number, number> = new Map();
          data.forEach((user: any) => {
            const level = user.level || 1;
            levelDistribution.set(level, (levelDistribution.get(level) || 0) + 1);
          });

          // Achievement counts
          let totalAchievements = 0;
          data.forEach((user: any) => {
            if (user.achievements && Array.isArray(user.achievements)) {
              totalAchievements += user.achievements.length;
            }
          });
          const avgAchievements = data.length > 0 ? totalAchievements / data.length : 0;

          setStats({
            totalUsers: data.length,
            totalXP,
            avgXP,
            maxXP,
            levelDistribution: Array.from(levelDistribution.entries())
              .sort((a, b) => a[0] - b[0])
              .map(([level, count]) => ({ level, count })),
            totalAchievements,
            avgAchievements,
          });
        }
      } catch (error) {
        console.error('Error loading gamification data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading gamification data...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '2rem' }}>
        <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
          Gamification Analytics
        </h1>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ color: '#666' }}>No gamification data available.</p>
        </div>
      </div>
    );
  }

  const levelData = {
    labels: stats.levelDistribution.map((d: any) => `Level ${d.level}`),
    datasets: [
      {
        label: 'Users',
        data: stats.levelDistribution.map((d: any) => d.count),
        backgroundColor: '#2563eb',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Gamification Analytics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Users</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.totalUsers}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total XP</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.totalXP.toLocaleString()}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg XP per User</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {Math.round(stats.avgXP).toLocaleString()}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Achievements</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.totalAchievements}
          </h2>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            Avg: {stats.avgAchievements.toFixed(1)} per user
          </p>
        </div>
      </div>

      {/* Level Distribution Chart */}
      {stats.levelDistribution.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Level Distribution</h3>
          <BarChart data={levelData} height={300} />
        </div>
      )}

      {/* Top Users Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Top Users by XP
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>User ID</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Level</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Total XP</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Achievements</th>
            </tr>
          </thead>
          <tbody>
            {gamificationData
              .sort((a, b) => (b.totalXP || 0) - (a.totalXP || 0))
              .slice(0, 20)
              .map((user: any, index: number) => {
                const userEmail = user.id ? userEmailMap.get(user.id) || 'N/A' : 'N/A';
                return (
                  <tr key={user.id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                      {user.id || 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{userEmail}</td>
                    <td style={{ padding: '1rem' }}>{user.level || 1}</td>
                    <td style={{ padding: '1rem' }}>{(user.totalXP || 0).toLocaleString()}</td>
                    <td style={{ padding: '1rem' }}>
                      {user.achievements && Array.isArray(user.achievements)
                        ? user.achievements.length
                        : 0}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GamificationTab;

